/* eslint-disable react/destructuring-assignment */
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';
import {
  ContentfulRichTextGatsbyReference,
  RenderRichTextData,
  renderRichText,
} from 'gatsby-source-contentful/rich-text';
import { ReactElement, ReactNode } from 'react';

import Container from 'quarks/Container';
import Image, { ImageData } from 'quarks/Image';
import Link from 'quarks/Link';
import Text from 'quarks/Text';

import ComponentButton from 'components/Button/ComponentButton';
import ListItem from 'components/ListItem/ListItem';
import ComponentTestimonial from 'components/Testimonial/Testimonial';

import wrapRichTextElements from 'utils/richTextWrapper';

import type { CommonNode } from '@contentful/rich-text-react-renderer';
import type { Maybe, Scalars } from 'graphqlTypes';

export type ContentfulRichText = {
  __typename?: string;
  raw?: Maybe<Scalars['String']>;
  references?: Maybe<Array<Maybe<unknown>>>;
};

export type RichText = RenderRichTextData<ContentfulRichTextGatsbyReference>;

const heroRichTextParser = <T extends ContentfulRichText>(content: T) => {
  const options = {
    renderText: (text: ReactNode) => text,
    renderMark: {
      [MARKS.BOLD]: (text: ReactNode) => {
        console.log('text', text);

        return (
          <Text fontWeight="bold" textColor="blue-600">
            {text}
          </Text>
        );
      },
      [MARKS.ITALIC]: (text: ReactNode) => <Text fontStyle="italic">{text}</Text>,
      [MARKS.UNDERLINE]: (text: ReactNode) => <Text textDecoration="underline">{text}</Text>,
      [MARKS.CODE]: (text: ReactNode) => <Text>{text}</Text>,
      [MARKS.SUBSCRIPT]: (text: ReactNode) => <sub>{text}</sub>,
      [MARKS.SUPERSCRIPT]: (text: ReactNode) => <sup>{text}</sup>,
    },
    renderNode: {
      [BLOCKS.PARAGRAPH]: (node: CommonNode, children: ReactNode) => <>{children}</>,
      [BLOCKS.HEADING_1]: (node: CommonNode, children: ReactNode) => <>{children}</>,
      [BLOCKS.HEADING_2]: (node: CommonNode, children: ReactNode) => <>{children}</>,
      [BLOCKS.HEADING_3]: (node: CommonNode, children: ReactNode) => <>{children}</>,
      [BLOCKS.HEADING_4]: (node: CommonNode, children: ReactNode) => <>{children}</>,
      [BLOCKS.HEADING_5]: (node: CommonNode, children: ReactNode) => <>{children}</>,
      [BLOCKS.HEADING_6]: (node: CommonNode, children: ReactNode) => <>{children}</>,
      [BLOCKS.EMBEDDED_ASSET]: (node: CommonNode) => {
        const { data } = node;

        return <Image image={data.target} alt={data.target.title} />;
      },
      [BLOCKS.EMBEDDED_ENTRY]: (node: CommonNode) => {
        const {
          data: { target },
        } = node;
        if (!target) {
          return null;
        }

        switch (target.__typename) {
          case 'ContentfulComponentCallToAction':
            return <ComponentButton {...target} />;
          case 'ContentfulComponentTestimonial':
            return <ComponentTestimonial {...target} />;
          case 'ContentfulComponentImage':
            return (
              <Image
                width="100%"
                maxWidth="100%"
                image={(target?.bynderImageDesktop as ImageData) || (target?.imageDesktop as ImageData)}
                alt={target?.alt || 'Reference Image'}
                boxShadow={target?.boxShadow}
                roundedCorners={target?.roundedCorners}
              />
            );
          default:
            return null;
        }
      },
      [INLINES.EMBEDDED_ENTRY]: (node: CommonNode) => {
        const {
          data: { target },
        } = node;
        if (!target) {
          return null;
        }

        switch (target.__typename) {
          case 'ContentfulComponentImage':
            return (
              <Container width="400px" marginY={32}>
                <Image
                  width="100%"
                  image={(target?.bynderImageDesktop as ImageData) || (target?.imageDesktop as ImageData)}
                  alt={target?.alt || 'Reference Image'}
                  boxShadow={target?.boxShadow}
                  roundedCorners={target?.roundedCorners}
                />
              </Container>
            );
          case 'ContentfulElementListItem':
            return (
              <ListItem
                {...target}
                marginY={32}
                backgroundColor="gray-200"
                paddingAll={32}
                borderRadius="4px"
                maxWidth="100%"
                showIcon={false}
                css={`
                  > div > span {
                    margin-bottom: 15px;
                    ~ div > ul {
                      margin-left: 10px;
                    }
                  }
                `}
              />
            );
          default:
            return null;
        }
      },
      [INLINES.HYPERLINK]: (node: CommonNode, children: ReactNode) => {
        const { data } = node;

        return (
          <Link
            href={data.uri}
            textDecoration="none"
            textColor="navy-700"
            fontWeight="bold"
            hover={{ textDecoration: 'underline', textColor: 'navy-900' }}
            focus={{ textColor: 'navy-700', textDecoration: 'none' }}
          >
            {children}
          </Link>
        );
      },
    },
  };

  return wrapRichTextElements(renderRichText(content as RichText, options) as ReactElement[], []);
};

export default heroRichTextParser;
